import { Api } from '@services';
import { useEffect, useState, useRef } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import styled from 'styled-components';

const StyledWrapper = styled.div<{ direction: string }>`
  display: grid;
  grid-template-columns: ${(props) => (props.direction === 'row' ? '2fr 2fr' : 'none')};
  grid-template-rows: ${(props) => (props.direction === 'column' ? 'auto' : 'none')};
  grid-gap: ${(props) => (props.direction === 'column' ? '.8rem' : '1rem')};
  padding: ${(props) => (props.direction === 'column' ? '0 0 .4rem 0' : '0')};
`;

export function IdentifiersInput({
  direction,
  valid,
  invalid,
  disabled,
  initFilter,
  onFilterChange,
}) {
  const [keyCache, setKeyCache] = useState({});
  const [isLoadingKey, setIsLoadingKey] = useState(false);
  const [keyOptions, setKeyOptions] = useState<any[]>([]);
  const [key, setKey] = useState<string>('');

  const [valueCache, setValueCache] = useState({});
  const [isLoadingValue, setIsLoadingValue] = useState(false);
  const [valueOptions, setValueOptions] = useState<any[]>([]);
  const [value, setValue] = useState<string>('');

  const valueTypeaheadRef = useRef<any>(null);

  useEffect(() => {
    if (key !== '' && value !== '') {
      onFilterChange(`identifiers(${key}=${value})`);
    }
  }, [key, value]);

  const handleKeySearch = (query: string) => {
    if (keyCache[query]) {
      setKeyOptions(keyCache[query]);
      return;
    }
    setIsLoadingKey(true);
    Api.get(`/v2/assets/filters/identifiers`, {
      params: {
        key: query,
      },
    })
      .then((resp) => {
        const items = Object.keys(resp.data).map((k, i) => ({
          id: i,
          name: k,
        }));

        setKeyOptions(items);
        setKeyCache((prev) => ({ ...prev, [query]: items }));
      })
      .finally(() => {
        setIsLoadingKey(false);
      });
  };

  const handleKeyChange = (selected: any[]) => {
    if (selected.length > 0) {
      const newKey = selected[0].label;
      if (newKey !== key) {
        setValue('');
        setValueOptions([]);
        onFilterChange(null);

        if (valueTypeaheadRef.current) {
          valueTypeaheadRef.current.clear();
        }
      }

      setKey(newKey);
    } else {
      setKey('');
      setValue('');
      setValueOptions([]);
      onFilterChange(null);

      if (valueTypeaheadRef.current) {
        valueTypeaheadRef.current.clear();
      }
    }
  };

  const handleValueSearch = (query: string) => {
    const cacheKey = `${key}__${query}`;
    if (valueCache[cacheKey]) {
      setValueOptions(valueCache[cacheKey]);
      return;
    }

    setIsLoadingValue(true);

    Api.get<Record<string, string[]>>(`/v2/assets/filters/identifiers`, {
      params: { key },
    })
      .then((resp) => {
        const rawValues: string[] = resp.data[key] ?? [];
        const filtered = rawValues.filter((v) => v.toLowerCase().includes(query.toLowerCase()));
        const items = filtered.map((v, i) => ({ id: i, name: v }));
        setValueOptions(items);
        setValueCache((prev) => ({ ...prev, [cacheKey]: items }));
      })
      .finally(() => {
        setIsLoadingValue(false);
      });
  };

  const handleValueChange = (selected: any[]) => {
    if (selected.length > 0) {
      setValue(selected[0].label);
    } else {
      setValue('');
      onFilterChange(null);
    }
  };

  return (
    <StyledWrapper direction={direction}>
      <AsyncTypeahead
        className="w-100"
        defaultInputValue={initFilter.length > 0 ? initFilter.split('=')[0] : ''}
        delay={500}
        disabled={disabled}
        filterBy={() => true}
        id="identifier-key"
        isInvalid={invalid}
        isLoading={isLoadingKey}
        isValid={valid}
        maxResults={25}
        minLength={1}
        onChange={handleKeyChange}
        onSearch={handleKeySearch}
        options={keyOptions.map((o) => ({ id: o.id, label: o.name }))}
        paginate
        placeholder="Search for a Key..."
        renderMenuItemChildren={(option, { text }) => (
          <div title={option.label}>
            <Highlighter search={text}>{option.label}</Highlighter>
            <div>
              <small className="filter-sm-name">Identifier ID: {option.id}</small>
            </div>
          </div>
        )}
        useCache={false}
      />
      <AsyncTypeahead
        className="w-100"
        ref={valueTypeaheadRef}
        defaultInputValue={initFilter.length > 0 ? initFilter.split('=')[1] : ''}
        delay={500}
        disabled={disabled || !key}
        filterBy={() => true}
        id="identifier-value"
        isInvalid={invalid}
        isLoading={isLoadingValue}
        isValid={valid}
        maxResults={25}
        minLength={0}
        onChange={handleValueChange}
        onSearch={handleValueSearch}
        options={valueOptions.map((o) => ({ id: o.id, label: o.name }))}
        paginate
        placeholder="Select Value"
        onFocus={() => {
          if (key) {
            handleValueSearch('');
          }
        }}
        renderMenuItemChildren={(option, { text }) => (
          <div title={option.label}>
            <Highlighter search={text}>{option.label}</Highlighter>
          </div>
        )}
        useCache={false}
      />
    </StyledWrapper>
  );
}
